// src/components/kanban/board-utils.ts
/**
 * Funzioni pure di supporto per la Kanban board dinamica (drag & drop, split task
 * attivi/completati, id dnd-kit). Nessun hook React qui: solo logica testabile in isolamento.
 * `moveTaskInBoard` per l'update ottimistico resta invece locale a `use-kanban-columns.ts`
 * (Part 1), che l'ha introdotta prima che questo file esistesse.
 */
import { pointerWithin, rectIntersection, type CollisionDetection } from '@dnd-kit/core';
import type { DynamicKanbanBoard, DynamicKanbanColumnData, ProjectTaskSummary } from '../../types/api';
import { isTaskCompleted } from '../../utils/task-status-utils';

const TASK_DND_PREFIX = 'task-';
const COLUMN_DND_PREFIX = 'column-';

export const taskDndId = (id: number) => `${TASK_DND_PREFIX}${id}`;
export const columnDndId = (id: number) => `${COLUMN_DND_PREFIX}${id}`;

export function parseTaskDndId(id: string | number): number | null {
  const s = String(id);
  if (!s.startsWith(TASK_DND_PREFIX)) return null;
  const n = Number(s.slice(TASK_DND_PREFIX.length));
  return Number.isFinite(n) ? n : null;
}

export function parseColumnDndId(id: string | number): number | null {
  const s = String(id);
  if (!s.startsWith(COLUMN_DND_PREFIX)) return null;
  const n = Number(s.slice(COLUMN_DND_PREFIX.length));
  return Number.isFinite(n) ? n : null;
}

/**
 * Divide i task di una colonna in attivi (ordinati per kanbanPosition crescente) e
 * completati (ordinati per data di completamento decrescente; quelli senza data in fondo).
 */
export function splitColumnTasks(column: DynamicKanbanColumnData): {
  active: ProjectTaskSummary[];
  completed: ProjectTaskSummary[];
} {
  const active: ProjectTaskSummary[] = [];
  const completed: ProjectTaskSummary[] = [];

  for (const task of column.tasks) {
    if (isTaskCompleted(task)) {
      completed.push(task);
    } else {
      active.push(task);
    }
  }

  active.sort((a, b) => (a.kanbanPosition ?? 0) - (b.kanbanPosition ?? 0));
  completed.sort((a, b) => {
    if (!a.completedAt && !b.completedAt) return 0;
    if (!a.completedAt) return 1;
    if (!b.completedAt) return -1;
    return new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime();
  });

  return { active, completed };
}

/** Trova la colonna che contiene un dato task nello snapshot board corrente. */
export function findTaskColumn(board: DynamicKanbanBoard, taskId: number): DynamicKanbanColumnData | undefined {
  return board.columns.find((col) => col.tasks.some((t) => t.id === taskId));
}

/**
 * Collision detection custom per la board: preferisce sempre un task sotto il puntatore
 * rispetto alla colonna che lo contiene, così è possibile inserire un task in una
 * posizione precisa anche quando l'area della colonna è più grande della card. Fallback
 * su `rectIntersection` quando il puntatore non interseca nulla (drag molto rapidi).
 */
export const boardCollisionDetection: CollisionDetection = (args) => {
  const pointerHits = pointerWithin(args);
  if (pointerHits.length > 0) {
    const taskHit = pointerHits.find((hit) => parseTaskDndId(hit.id) != null);
    return taskHit ? [taskHit] : pointerHits;
  }

  const rectHits = rectIntersection(args);
  if (rectHits.length > 0) {
    const taskHit = rectHits.find((hit) => parseTaskDndId(hit.id) != null);
    return taskHit ? [taskHit] : rectHits;
  }

  return [];
};
